import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { FormSelectFieldDS } from 'components/reusable/form/FormFields'
import { Button } from 'slab'
import { getGetAdminEmployersEmployerIdComplianceAuditsQueryKey, usePostAdminEmployersEmployerIdComplianceAudits } from 'utils/web_api'
import { type AdminComplianceAudit, type PostAdminEmployersEmployerIdComplianceAuditsAuditType } from 'utils/web_api.schemas'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet'

interface BankPartnerFormData {
  selectedPartner: string
}

interface EmployerComplianceBankPartnerSheetProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  employerId: string
  availablePartnerOptions: string[]
}

const EmployerComplianceBankPartnerSheet = ({
  isOpen,
  setIsOpen,
  employerId,
  availablePartnerOptions,
}: EmployerComplianceBankPartnerSheetProps) => {
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<BankPartnerFormData>({
    defaultValues: {
      selectedPartner: '',
    }
  })

  const selectedPartner = watch('selectedPartner')

  const createAuditMutation = usePostAdminEmployersEmployerIdComplianceAudits({
    mutation: {
      onSuccess: (newAudit: AdminComplianceAudit) => {
        // Update the cache with the new audit
        queryClient.setQueryData(
          getGetAdminEmployersEmployerIdComplianceAuditsQueryKey(employerId),
          (oldData: AdminComplianceAudit[] | undefined) => {
            if (!oldData) return [newAudit]
            return [...oldData, newAudit]
          }
        )
        toast.success('Bank partner added successfully')
        setIsOpen(false)
        reset()
      },
      onError: (error: any) => {
        console.error('Failed to create audit:', error.message)
        toast.error(error.message)
      }
    }
  })

  const onSubmit = (data: BankPartnerFormData) => {
    createAuditMutation.mutate({
      employerId,
      data: { audit_type: data.selectedPartner as PostAdminEmployersEmployerIdComplianceAuditsAuditType }
    })
  }

  const handleClose = () => {
    setIsOpen(false)
    reset()
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className='h-dvh overflow-y-auto' style={{ minWidth: '500px' }} data-sheet='add-bank-partner'>
        <SheetHeader>
          <SheetTitle className='heading-lg'>
            Add Bank Partner
          </SheetTitle>
        </SheetHeader>
        <SheetDescription className='mb-4'>
          Select a Bank Partner to onboard this Employer to.
        </SheetDescription>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <FormSelectFieldDS
            fieldId='selectedPartner'
            fieldLabel='Bank Partner'
            placeholder='Select a bank partner'
            fieldOptions={availablePartnerOptions.map(
              partner => ({ label: partner.charAt(0).toUpperCase() + partner.slice(1), value: partner })
            )}
            register={register}
            validationRule={{ required: 'Bank partner selection is required' }}
            error={errors.selectedPartner}
          />

          {selectedPartner && selectedPartner === 'Bangor' && (
            <div className='p-3 bg-gray-50 rounded-md border border-gray-200'>
              <h3 className='text-md text-gray-600 mb-2 font-medium'>
                ⚠️ Onboarding to Bangor
              </h3>
              <p className='text-sm text-gray-600 mb-2'>Adding Bangor as a Bank Partner will do the following:</p>
              <ul className='text-sm text-gray-600 list-disc list-inside space-y-1'>
                <li>Initiate the identification of Owners in Employer's dashboard</li>
                <li>Block creation of new Unit Cards for Employees until 1/1</li>
                <li>Block creation of new Unit ACH accounts for on-Thatch Employees</li>
              </ul>
            </div>
          )}

          <div className='flex justify-end gap-3 pt-4'>
            <Button
              variant='secondary'
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              data-testid='add-bank-partner-button'
              type='submit'
              variant='primary'
              disabled={!isValid || createAuditMutation.isPending}
            >
              {createAuditMutation.isPending ? 'Adding...' : 'Add Bank Partner'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default EmployerComplianceBankPartnerSheet
